import { useSearchParams } from "react-router-dom";
import { useMemo, useEffect, useState } from "react";
import { Globe, FolderTree, Users, UsersRound, Monitor, ScrollText, ShieldCheck, Search, XCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { hasPermission } from "../../utils/permissionsHelper";

export interface ADTab {
    id: string;
    label: string;
    icon: React.ReactNode;
    permissionKey?: string;
}

export const AD_TABS: ADTab[] = [
    { id: "domain", label: "Domain", icon: <Globe size={16} />, permissionKey: "ad_domain" },
    { id: "ous", label: "OUs", icon: <FolderTree size={16} />, permissionKey: "ad_ous" },
    { id: "users", label: "Users", icon: <Users size={16} />, permissionKey: "ad_users" },
    { id: "groups", label: "Groups", icon: <UsersRound size={16} />, permissionKey: "ad_groups" },
    { id: "computers", label: "Computers", icon: <Monitor size={16} />, permissionKey: "ad_computers" },
    { id: "gpos", label: "GPOs", icon: <ScrollText size={16} />, permissionKey: "ad_gpos" },
    { id: "password-policies", label: "Password Policies", icon: <ShieldCheck size={16} />, permissionKey: "ad_password_policies" },
];

export function ActiveDirectorySidebar({
    tabs,
    activeTab,
    onTabChange,
}: {
    tabs: ADTab[];
    activeTab: string;
    onTabChange: (tabId: string) => void;
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [tabSearchTerm, setTabSearchTerm] = useState("");
    const [hoveredTab, setHoveredTab] = useState<{ label: string; top: number } | null>(null);

    const filteredTabs = useMemo(() => {
        if (!tabSearchTerm.trim()) return tabs;
        return tabs.filter((t) =>
            t.label.toLowerCase().includes(tabSearchTerm.toLowerCase())
        );
    }, [tabs, tabSearchTerm]);

    return (
        <>
            <div className={`bg-gray-900 border-r border-gray-800 flex flex-col h-full shrink-0 transition-all duration-300 relative ${isOpen ? "w-52" : "w-14"}`}>
                {/* Header */}
                <div className="shrink-0 border-b border-gray-800 p-2 space-y-2">
                    {isOpen ? (
                        <>
                            {/* Title & Collapse Button */}
                            <div className="flex items-center justify-between gap-2 h-8">
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="bg-orange-600/20 p-1 rounded-lg border border-orange-500/30 shrink-0">
                                        <Globe className="text-orange-400 h-4 w-4" />
                                    </div>
                                    <h1 className="text-xs font-bold text-white tracking-wide whitespace-nowrap truncate">
                                        Active Directory
                                    </h1>
                                </div>

                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="shrink-0 p-1 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-md transition-colors"
                                    title="Collapse Sidebar"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Search Input when Expanded */}
                            <div className="relative w-full">
                                <input
                                    type="text"
                                    placeholder="Search tabs..."
                                    value={tabSearchTerm}
                                    onChange={(e) => setTabSearchTerm(e.target.value)}
                                    className="w-full bg-gray-800 border border-gray-600 rounded-md px-2 py-1 pl-7 text-white placeholder-gray-500 focus:outline-none focus:border-orange-600 text-[11px]"
                                />
                                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-500" />
                                {tabSearchTerm && (
                                    <button
                                        onClick={() => setTabSearchTerm('')}
                                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                                    >
                                        <XCircle className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                        </>
                    ) : (
                        /* Collapsed Header: Only Open Button */
                        <div className="flex items-center justify-center h-8">
                            <button
                                onClick={() => setIsOpen(true)}
                                className="p-1.5 bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded-md transition-colors"
                                title="Expand Sidebar"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </button>
                        </div>
                    )}
                </div>

                {/* Tab List */}
                <div className="flex-1 overflow-y-auto overflow-x-hidden custom-scroll p-1.5 space-y-1">
                    {filteredTabs.length > 0 ? (
                        filteredTabs.map((tab) => (
                            <button
                                key={tab.id}
                                onMouseEnter={(e) => !isOpen && setHoveredTab({ label: tab.label, top: e.currentTarget.getBoundingClientRect().top })}
                                onMouseLeave={() => setHoveredTab(null)}
                                onClick={() => {
                                    onTabChange(tab.id);
                                    setHoveredTab(null);
                                }}
                                className={`w-full flex items-center h-8 rounded-md transition-all duration-200 relative group ${isOpen ? 'px-3 justify-start' : 'justify-center'} ${activeTab === tab.id
                                    ? 'bg-orange-700 text-white shadow-sm'
                                    : 'text-gray-200 hover:text-white hover:bg-gray-800'
                                    }`}
                            >
                                <span className="shrink-0 flex items-center justify-center w-5 h-5">
                                    {tab.icon}
                                </span>

                                <span
                                    className={`ml-3 font-bold text-[11px] uppercase tracking-wider whitespace-nowrap transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible absolute w-0 overflow-hidden'}`}
                                >
                                    {tab.label}
                                </span>
                            </button>
                        ))
                    ) : (
                        isOpen && (
                            <div className="text-center py-10">
                                <Search className="w-5 h-5 text-gray-700 mx-auto mb-2" />
                                <p className="text-gray-500 text-[10px] font-bold uppercase">No Matches</p>
                            </div>
                        )
                    )}
                </div>
            </div>

            {/* Floating Tooltip */}
            {!isOpen && hoveredTab && (
                <div
                    className="fixed z-[9999] px-2.5 py-1.5 bg-orange-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-md shadow-xl whitespace-nowrap pointer-events-none transition-opacity duration-200"
                    style={{ left: '4rem', top: hoveredTab.top + 2 }}
                >
                    {hoveredTab.label}
                    <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2 h-2 bg-orange-600 rotate-45 rounded-sm"></div>
                </div>
            )}
        </>
    );
}

// ─── Permission-aware parent ──────────────────────────────────────────────────
export function useADPermittedTabs() {
    return useMemo(() => {
        return AD_TABS.filter((tab) => {
            if (tab.permissionKey) {
                return hasPermission(tab.permissionKey, "read");
            }
            return true;
        });
    }, []);
}

export function useADActiveTab() {
    const [searchParams, setSearchParams] = useSearchParams();
    const permittedTabs = useADPermittedTabs();

    const requestedTab = searchParams.get("tab");

    const activeTab = useMemo(() => {
        const isValidTab = permittedTabs.some((tab) => tab.id === requestedTab);
        return isValidTab && requestedTab ? requestedTab : (permittedTabs[0]?.id || "");
    }, [requestedTab, permittedTabs]);

    useEffect(() => {
        if (permittedTabs.length > 0 && requestedTab !== activeTab) {
            setSearchParams({ tab: activeTab }, { replace: true });
        }
    }, [activeTab, requestedTab, permittedTabs, setSearchParams]);

    return {
        permittedTabs,
        activeTab,
        setTab: (id: string) => setSearchParams({ tab: id }),
    };
}
